import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const RefineIdeaModal = ({ isOpen, onClose, ideaId, refinementQuestions, onRefinementComplete }) => {
  const [messages, setMessages] = useState([])
  const [currentInput, setCurrentInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [conversationStarted, setConversationStarted] = useState(false)
  const messagesEndRef = useRef(null)
  const hasInitialized = useRef(false)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false
      setIsSubmitting(false)
      setIsTyping(false)
      setConversationStarted(false)
      return
    }
    
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true
      
      setMessages([])
      setCurrentInput('')
      setConversationStarted(false)
      
      const fetchInitialMessage = async () => {
        setIsTyping(true)
        try {
          const response = await apiService.sendRefinementChat(ideaId, [
            { role: 'user', content: 'Hello, I need help refining my idea.' }
          ])
          setMessages([
            { type: 'bot', text: response.message }
          ])
        } catch (error) {
          console.error('Failed to get initial message:', error)
          
          if (!refinementQuestions || refinementQuestions.length === 0) {
            console.error('No refinement questions available for fallback')
            setMessages([{ 
              type: 'bot', 
              text: "I'm having trouble loading the refinement guidance. Please try closing and reopening this dialog." 
            }])
          } else {
            setMessages([{ 
              type: 'bot', 
              text: `Based on the triage feedback, ${refinementQuestions[0].question}` 
            }])
          }
        } finally {
          setIsTyping(false)
          setConversationStarted(true)
        }
      }
      
      fetchInitialMessage()
    }
  }, [isOpen, ideaId])


  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!currentInput.trim() || isSubmitting || isTyping) return

    const userMessage = currentInput.trim()
    setMessages(prev => [...prev, { type: 'user', text: userMessage }])
    setCurrentInput('')
    setIsTyping(true)

    try {
      const chatMessages = messages
        .concat([{ type: 'user', text: userMessage }])
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.text
        }))

      console.log('Sending chat messages:', JSON.stringify(chatMessages, null, 2))
      const response = await apiService.sendRefinementChat(ideaId, chatMessages)
      
      // Check if AI returned completion JSON
      try {
        const completion = JSON.parse(response.message)
        if (completion.action === 'complete' && completion.field_updates) {
          // AI is done - auto-submit with field updates
          await handleAutoSubmit(completion.field_updates, completion.message)
          return
        }
      } catch {
        // Not JSON, just a regular message - continue conversation
      }
      
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: response.message 
      }])
    } catch (error) {
      console.error('Failed to get chat response:', error)
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: "I'm having trouble responding right now. Please try again or click 'I'm Done' to submit your refinements." 
      }])
    } finally {
      setIsTyping(false)
    }
  }



  const handleAutoSubmit = async (fieldUpdates, aiMessage) => {
    setIsSubmitting(true)
    setIsTyping(true)
    
    try {
      // Show AI's completion message
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: aiMessage 
      }])
      
      const idea = await apiService.getIdea(ideaId)
      
      // Intelligently update only the fields that were discussed
      const updatedIdea = { ...idea }
      const conversationHeader = "\n\n--- Refined in conversation with AI ---\n"
      
      Object.entries(fieldUpdates).forEach(([field, content]) => {
        if (content && content.trim()) {
          // Add conversation header before AI-refined content
          updatedIdea[field] = (idea[field] || '') + conversationHeader + content.trim()
        }
      })

      await apiService.updateIdea(ideaId, updatedIdea)
      
      setIsTyping(false)
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: "Your idea has been updated and submitted for re-evaluation." 
      }])
      
      setTimeout(() => {
        onClose()
        onRefinementComplete?.(updatedIdea)
      }, 1500)
      
    } catch (error) {
      console.error('Failed to submit refinement:', error)
      setIsTyping(false)
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: "I encountered an error submitting your refinement. Please try again." 
      }])
      setIsSubmitting(false)
    }
  }


  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl bg-gradient-to-br from-gray-700/80 to-gray-700/80 shadow-none outline -outline-offset-1 outline-white/10 relative overflow-visible max-w-4xl w-full h-[600px] flex flex-col mt-48">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-blue-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
        
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-64 h-64 z-50">
          <img 
            src={`${import.meta.env.BASE_URL}characters/Orin/1_Default/1_thumbup.png`}
            alt="Orin"
            className="w-full h-full object-contain drop-shadow-2xl"
          />
        </div>
        
        <div className="px-5 py-6 sm:px-6 border-b border-white/10 relative z-10">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">
              Refine Your Idea
            </h2>
            {!isSubmitting && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6 relative z-10">
          <div className="space-y-4 mb-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  msg.type === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800/80 text-gray-100'
                }`}>
                  <div className="whitespace-pre-wrap break-words leading-relaxed">
                    {msg.text.split(/(\*\*.*?\*\*)/).map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i}>{part.slice(2, -2)}</strong>
                      }
                      return part
                    })}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg px-4 py-3 bg-gray-800/90 border border-gray-700/50">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {!isSubmitting && (
          <div className="px-5 py-4 sm:px-6 border-t border-white/10 relative z-10">
            {conversationStarted && (
              <form onSubmit={handleSubmit} className="flex gap-3">
                <textarea
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  disabled={isTyping || isSubmitting}
                  className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  rows="1"
                />
                <button
                  type="submit"
                  disabled={!currentInput.trim() || isTyping || isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 text-sm font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

RefineIdeaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ideaId: PropTypes.string.isRequired,
  refinementQuestions: PropTypes.array,
  onRefinementComplete: PropTypes.func
}

export default RefineIdeaModal
